import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DataFhirAdapter,
  DataFhirCategorySetting,
  DataFhirModel,
} from '../data.model';
import { DictionariesCollectionService } from '../dictionaries/dictionaries-collection.service';
import {
  Practitioner,
  PractitionerAdapter,
} from '../persons/practitioner.model';
import { Provider } from './base-provider.model';
import { Organization, OrganizationAdapter } from './organization.model';

export type PractitionerProvider = DataFhirModel<fhir4.PractitionerRole> &
  Provider & {
    practitioner: Practitioner;
    organization: Organization;
  };

@Injectable({
  providedIn: 'root',
})
export class PractitionerProviderAdapter extends DataFhirAdapter<PractitionerProvider> {
  categorySettings: DataFhirCategorySetting<PractitionerProvider>[] = [
    {
      categoryName: 'facility',
      dictionaryIds: ['1.2.276.0.76.11.31--20211015121326'], // valueSetName: IHEXDSauthorSpeciality
    },
    {
      categoryName: 'specialty',
      dictionaryIds: ['1.2.276.0.76.11.37--20190517134631'], // valueSetName: IHEXDSpracticeSettingCode
    },
  ];

  constructor(
    protected dictionariesCollection: DictionariesCollectionService,
    private practitionerAdapter: PractitionerAdapter,
    private organizationAdapter: OrganizationAdapter,
  ) {
    super('PractitionerRole', dictionariesCollection);
  }

  fromResource$(
    practitionerRole: fhir4.PractitionerRole,
    responseBundle?: fhir4.Bundle,
  ): Observable<PractitionerProvider> {
    const responseEntries = responseBundle?.entry || [];

    const [, practitionerId] =
      practitionerRole.practitioner.reference.split('/');
    const [, organizationId] =
      practitionerRole.organization.reference.split('/');

    const includedPractitionerResource = responseEntries.find(
      ({ resource }) =>
        resource.resourceType === 'Practitioner' &&
        resource.id === practitionerId,
    )?.resource as fhir4.Practitioner;
    const includedOrganizationResource = responseEntries.find(
      ({ resource }) =>
        resource.resourceType === 'Organization' &&
        resource.id === organizationId,
    )?.resource as fhir4.Organization;

    // PractitionerRole should include Practitioner and Organization!!!
    if (!includedPractitionerResource || !includedOrganizationResource)
      return of();

    return forkJoin([
      super.fromResource$(practitionerRole),
      this.practitionerAdapter.fromResource$(includedPractitionerResource),
      this.organizationAdapter.fromResource$(includedOrganizationResource),
    ]).pipe(
      map(([baseItem, practitioner, organization]) => {
        /** @todo use identifier from practitionerRole as soon as it is supported in server responses */
        const identifier = practitioner.identifier;

        const name = practitioner.nameDetails.fullName;
        // const facility = organization.name;
        const specialty = practitioner.specialty.length
          ? practitioner.specialty
          : ['-'];

        // const organizationName = organization.name;
        const address = organization.address;
        const parentOrganizationId = organization.parentOrganizationId;

        //TODO: remove string from facility and organization Name after go from Bauer
        const provider: Provider = {
          providerType: 'practitioner',
          identifier,
          name,
          organizationName: '',
          facility: this.resolveTopOrganizationName(
            includedOrganizationResource,
            responseEntries,
          ),
          specialty,
          address,
          parentOrganizationId,
        };

        return {
          ...baseItem,
          ...provider,
          practitioner,
          organization,
        };
      }),
    );
  }

  private resolveTopOrganizationName(
    organization: fhir4.Organization,
    responseEntries: fhir4.BundleEntry[],
  ): string {
    let current = organization;
    while (current.partOf?.reference) {
      const [, parentId] = current.partOf.reference.split('/');
      const parent = responseEntries.find(
        ({ resource }) =>
          resource?.resourceType === 'Organization' && resource.id === parentId,
      )?.resource as fhir4.Organization;
      if (!parent) break;
      current = parent;
    }
    return current.name || '';
  }

  protected blankResource(): fhir4.PractitionerRole {
    return null;
  }

  toResource(practitionerRole: PractitionerProvider): fhir4.PractitionerRole {
    return null;
  }
}
